import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowRight } from "lucide-react";

import { Breadcrumbs } from "@/components/breadcrumbs";
import { Button } from "@/components/ui/button";
import { SiteHeader, SiteFooter } from "@/components/site-chrome";
import { MobileCtaBar } from "@/components/landing/mobile-cta-bar";
import { pillars, steps } from "@/content/approach";
import { breadcrumbJsonLd, SITE_URL, BRAND_OG_IMAGE, BRAND_OG_IMAGE_ALT, SITE_NAME } from "@/lib/seo";
import { useTranslation } from "react-i18next";

const title = "How Our Online English Classes Work | Smart Word Labs";
const description =
  "Free assessment, a written learning plan, live micro-cohorts of 5 or one-on-one IELTS coaching, plus weekly homework and progress feedback.";

export const Route = createFileRoute("/how-it-works")({
  head: () => ({
    meta: [
      { title },
      { name: "description", content: description },
      { property: "og:title", content: title },
      { property: "og:description", content: description },
      { property: "og:type", content: "website" },
      { property: "og:url", content: `${SITE_URL}/how-it-works` },
      { property: "og:site_name", content: SITE_NAME },
      { property: "og:image", content: BRAND_OG_IMAGE },
      { property: "og:image:alt", content: BRAND_OG_IMAGE_ALT },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:image", content: BRAND_OG_IMAGE },
      { name: "twitter:image:alt", content: BRAND_OG_IMAGE_ALT },
      { name: "twitter:title", content: title },
      { name: "twitter:description", content: description },
    ],
    links: [{ rel: "canonical", href: `${SITE_URL}/how-it-works` }],
    scripts: [breadcrumbJsonLd([{ name: "How It Works", path: "/how-it-works" }])],
  }),
  component: HowItWorks,
});

function HowItWorks() {
  const { t } = useTranslation("pages");
  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <Breadcrumbs trail={[{ name: t("howItWorks.breadcrumb"), path: "/how-it-works" }]} />

      <main className="pb-20 lg:pb-0">
        <section className="mx-auto max-w-4xl px-5 py-12">
          <h1 className="text-4xl leading-tight text-primary sm:text-5xl">
            {t("howItWorks.hero.title")}
          </h1>
          <p className="mt-5 text-lg leading-relaxed text-muted-foreground">
            {t("howItWorks.hero.subtitle")}
          </p>
        </section>

        <section className="border-y border-border bg-secondary/50 py-16">
          <div className="mx-auto max-w-6xl px-5">
            <h2 className="text-3xl text-primary sm:text-4xl">{t("howItWorks.pillars.title")}</h2>
            <div className="mt-10 grid gap-10 md:grid-cols-3">
              {pillars.map((p) => (
                <div key={p.title} className="group">
                  <div className="flex items-start gap-4">
                    <span className="grid size-12 shrink-0 place-items-center rounded-xl bg-background text-chart-3 transition-colors duration-300 group-hover:bg-accent/20">
                      <p.icon className="size-5" />
                    </span>
                    <h3 className="min-w-0 text-xl leading-snug text-primary">{p.title}</h3>
                  </div>
                  <p className="mt-4 text-base leading-relaxed text-muted-foreground">{p.body}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="mx-auto max-w-4xl px-5 py-16">
          <h2 className="text-3xl text-primary sm:text-4xl">{t("howItWorks.steps.title")}</h2>
          <ol className="mt-8 space-y-6">
            {steps.map((s) => (
              <li key={s.title} className="surface-card p-6">
                <h3 className="text-lg text-primary">{s.title}</h3>
                <p className="mt-2 leading-relaxed text-muted-foreground">{s.body}</p>
              </li>
            ))}
          </ol>

          <div className="mt-10 flex flex-wrap items-center gap-x-6 gap-y-3">
            <Button variant="hero" size="xl" asChild>
              <Link to="/book">{t("howItWorks.cta.book")}</Link>
            </Button>
            <Link
              to="/programmes"
              className="group inline-flex items-center gap-2 text-base font-semibold text-primary transition-colors hover:text-accent"
            >
              {t("howItWorks.cta.browsePrograms")}
              <ArrowRight className="size-4 transition-transform group-hover:translate-x-1" />
            </Link>
            <Link
              to="/pricing"
              className="group inline-flex items-center gap-2 text-base font-semibold text-primary transition-colors hover:text-accent"
            >
              {t("howItWorks.cta.seePricing")}
              <ArrowRight className="size-4 transition-transform group-hover:translate-x-1" />
            </Link>
          </div>
        </section>
      </main>

      <SiteFooter />
      <MobileCtaBar />
    </div>
  );
}
